package org.lennycodes.portfolio.styles

import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.functions.LinearGradient
import com.varabyte.kobweb.compose.css.functions.linearGradient
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.backgroundImage
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.boxShadow
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.translateY
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.selectors.active
import com.varabyte.kobweb.silk.style.selectors.focus
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.theme.colors.ColorPalettes
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.lennycodes.gradematewebsite.components.style.GradeMateColors

// ----- Styles -----

val LoginPageStyle = CssStyle {
    base {
        Modifier
            .fillMaxSize()
            .minHeight(100.vh)
            .display(DisplayStyle.Flex)
            .styleModifier {
                property("align-items", "center")
                property("justify-content", "center")
                property(
                    "background",
                    "linear-gradient(135deg, ${GradeMateColors.Secondary} 0%, ${GradeMateColors.Background} 100%)"
                )
            }
    }
}

val LoginCardStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .maxWidth(400.px)
            .backgroundColor(Colors.White)
            .borderRadius(20.px)
            .styleModifier {
                property("box-shadow", "0 20px 60px ${Color.rgba(0, 0, 0, 0.25f)}")
            }
    }
}

val LoginLogoStyle = CssStyle {
    base {
        Modifier
            .margin(0.px, 0.px, 16.px)
            .styleModifier { property("border-radius", "12px") }
    }
}

val LoginTitleStyle = CssStyle {
    base {
        Modifier
            .fontSize(24.px)
            .fontWeight(FontWeight.Bold)
            .color(GradeMateColors.PrimaryDark)
            .textAlign(TextAlign.Center)
            .margin(0.px, 0.px, 4.px)
            .fontFamily("Playfair Display", "serif")
    }
}

val LoginSubtitleStyle = CssStyle {
    base {
        Modifier
            .fontSize(14.px)
            .color(GradeMateColors.TextSecondary)
            .textAlign(TextAlign.Center)
            .margin(0.px, 0.px, 28.px)
    }
}

val LoginFieldGroupStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .margin(0.px, 0.px, 18.px)
            .display(DisplayStyle.Flex)
            .styleModifier { property("flex-direction", "column") }
    }
}

val LoginLabelStyle = CssStyle {
    base {
        Modifier
            .fontSize(13.px)
            .fontWeight(FontWeight.SemiBold)
            .color(GradeMateColors.Secondary)
            .margin(0.px, 0.px, 6.px)
    }
}

val LoginInputStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .fontSize(15.px)
            .styleModifier {
                property("border", "1.5px solid #E0E0E0")
                property("border-radius", "10px")
                property("outline", "none")
                property("box-sizing", "border-box")
                property("transition", "border-color 200ms ease, box-shadow 200ms ease")
            }
    }
    cssRule(":focus") {
        Modifier.styleModifier {
            property("border-color", "${GradeMateColors.Primary}")
            property("box-shadow", "0 0 0 3px ${Color.rgba(25, 118, 210, 0.15f)}")
        }
    }
}

val LoginButtonStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .margin(8.px, 0.px, 0.px)
            .backgroundImage(
                linearGradient(
                    GradeMateColors.Primary,
                    GradeMateColors.Secondary,
                    LinearGradient.Direction.ToRight
                )
            )
            .color(GradeMateColors.TextOnDarkSecondary)
            .fontSize(16.px)
            .fontWeight(FontWeight.SemiBold)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("border", "none")
                property("border-radius", "10px")
                property("transition", "background-color 200ms ease, opacity 200ms ease, transform 150ms ease")
            }
    }
    hover {
        Modifier
            // reversed gradient instead of a flat backgroundColor,
            // so the swap is actually visible on top of backgroundImage
            .backgroundImage(
                linearGradient(
                    GradeMateColors.Secondary,
                    GradeMateColors.Background,
                    LinearGradient.Direction.ToRight
                )
            )
            .boxShadow(
                offsetY = 4.px,
                blurRadius = 16.px,
                color = Color.rgba(0, 0, 0, 0.15f) // swap for an accent-tinted rgba if you want a brand glow
            )
            .translateY((-2).px)
    }

    active {
        Modifier.translateY(0.px)
    }

    focus {
        Modifier.boxShadow(
            spreadRadius = 2.px,
            color = GradeMateColors.Secondary
        )
    }

}

val LoginErrorStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .margin(16.px, 0.px, 0.px)
            .fontSize(13.px)
            .textAlign(TextAlign.Center)
            .styleModifier {
                property("background-color", "${Color.rgba(229, 57, 53, 0.1f)}")
                property("color", "#C62828")
                property("border-radius", "8px")
            }
    }
}